// Keep the day-count boundary aligned with api/src/utils/retroactivity-window.ts.
// A date exactly N days before today is still allowed.
import dayjs from "dayjs";
import { flatMap } from "lodash";
import moment from "moment";
import { INSURANCE_PLAN_PARAMS_RULES } from "utils/constants";
import {
  buildInsurancePlanRule,
  generateForcedSQLCondition,
} from "utils/helpers/graphql";

const isEnabledRule = (rule) =>
  rule &&
  rule.name === INSURANCE_PLAN_PARAMS_RULES.RETROACTIVITY_ENABLED &&
  (rule.value === true || rule.value === "true");

const positiveDays = (rules) =>
  (rules || [])
    .filter(
      (rule) =>
        rule && rule.name === INSURANCE_PLAN_PARAMS_RULES.RETROACTIVITY_DAYS,
    )
    .map((rule) => Number(rule.value))
    .filter((value) => Number.isFinite(value) && value > 0);

export const getRetroactivityDays = ({ insurancePlans, now = dayjs() }) => {
  const enabledParams = flatMap(
    insurancePlans,
    (plan) => plan.params || [],
  ).filter((param) => ((param && param.rules) || []).some(isEnabledRule));
  const values = enabledParams.flatMap((param) => positiveDays(param.rules));
  if (values.length === 0) return null;
  const maxDays = Math.max(...values);
  const includesMonthDefault = enabledParams.some(
    (param) => positiveDays(param.rules).length === 0,
  );
  if (!includesMonthDefault) return maxDays;
  // An empty day count keeps the 1st of the month. That date is earlier
  // than today-minus-N when N is smaller than the distance to the 1st.
  const dayFloor = now.startOf("day").subtract(maxDays, "days");
  if (now.startOf("month").isBefore(dayFloor, "day")) return null;
  return maxDays;
};

export const getPlanRetroactivityDays = (insurancePlan) => {
  const values = flatMap(
    (insurancePlan && insurancePlan.params) || [],
    (param) => positiveDays(param && param.rules),
  );
  if (values.length === 0) return null;
  return Math.max(...values);
};

export const getRetroactivityMinDate = (retroactivityDays, now = dayjs()) => {
  if (!retroactivityDays || retroactivityDays <= 0) {
    return now.startOf("month");
  }
  return now.startOf("day").subtract(Number(retroactivityDays), "days");
};

export const getDefaultRetroactiveIssuanceDate = (
  retroactivityDays,
  now = dayjs(),
) => {
  const firstOfMonth = now.startOf("month");
  const minDate = getRetroactivityMinDate(retroactivityDays, now);
  if (firstOfMonth.isBefore(minDate, "day")) return minDate;
  return firstOfMonth;
};

export const isPlanOutsideRetroactivityWindow = ({
  insurancePlan,
  issuanceDate,
  now = moment(),
}) => {
  const today = moment(now).startOf("day");
  const issuance = moment(issuanceDate).startOf("day");
  const daysBack = today.diff(issuance, "days");
  if (daysBack <= 0) return false;
  const planDays = getPlanRetroactivityDays(insurancePlan);
  if (planDays === null) {
    return issuance.isBefore(today.clone().startOf("month"));
  }
  return planDays < daysBack;
};

export const buildRetroactivityPlanFilters = ({
  retroactive,
  issuanceDate,
  now = moment(),
}) => {
  if (!retroactive) return [];
  const filters = [
    generateForcedSQLCondition(
      INSURANCE_PLAN_PARAMS_RULES.RETROACTIVITY_ENABLED,
      "",
      true,
    ),
  ];
  const today = moment(now).startOf("day");
  const issuance = moment(issuanceDate).startOf("day");
  const daysBack = today.diff(issuance, "days");
  if (daysBack <= 0) return filters;
  const beforeCurrentMonth = issuance.isBefore(today.clone().startOf("month"));
  filters.push(
    buildInsurancePlanRule({
      rule: INSURANCE_PLAN_PARAMS_RULES.RETROACTIVITY_DAYS,
      predicate: beforeCurrentMonth ? "numericGteRequired" : "numericGte",
      value: daysBack,
      required: beforeCurrentMonth,
    }),
  );
  return filters.filter(Boolean);
};
